import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  SHIPMENT_STAGES,
  ShipmentIdCell,
  ShipmentNoteCell,
  ShipmentTariffCell,
  STAGE_ROUTE,
} from '#/entities/shipment'
import type { ShipmentRow } from '#/entities/shipment'
import { CustomerSmsBadge } from '#/entities/number-health'
import type { DataTableColumn } from '#/shared/ui/data-table'
import {
  DateCell,
  CustomerCell,
  VehicleCell,
  LocationCell,
  OwnerCell,
} from '#/shared/ui/table-cells'

interface UseLeadsColumnsOptions {
  showOwner: boolean
}

function idColumn(t: TFunction): DataTableColumn<ShipmentRow> {
  const route = STAGE_ROUTE[SHIPMENT_STAGES.LEAD]
  return {
    key: 'id',
    header: t('table.columns.id'),
    render: (row) => (
      <ShipmentIdCell
        shipmentId={row.shipmentId}
        code={row.code}
        badges={row.badges}
        to={route.to}
        params={{ [route.paramKey]: row.shipmentId }}
      />
    ),
  }
}

function createdColumn(t: TFunction): DataTableColumn<ShipmentRow> {
  return {
    key: 'created',
    header: t('table.columns.created'),
    render: (row) => <DateCell date={row.createdDate} time={row.createdTime} />,
  }
}

function noteColumn(t: TFunction): DataTableColumn<ShipmentRow> {
  return {
    key: 'note',
    header: t('table.columns.note'),
    render: (row) => (
      <ShipmentNoteCell shipmentId={row.shipmentId} count={row.noteCount} />
    ),
    className: 'w-14',
  }
}

function ownerColumn(t: TFunction): DataTableColumn<ShipmentRow> {
  return {
    key: 'owner',
    header: t('table.columns.owner'),
    render: (row) => (
      <OwnerCell avatarUrl={row.ownerAvatarUrl} name={row.ownerName} />
    ),
  }
}

function customerColumn(t: TFunction): DataTableColumn<ShipmentRow> {
  return {
    key: 'customer',
    header: t('table.columns.customer'),
    render: (row) => (
      <CustomerCell
        name={row.customerName}
        phone={row.customerPhone}
        phoneAccessory={
          <CustomerSmsBadge customer={row.customerDeliverability} />
        }
        shipmentId={row.shipmentId}
        customerId={row.customerId}
      />
    ),
  }
}

function vehicleColumn(t: TFunction): DataTableColumn<ShipmentRow> {
  return {
    key: 'vehicle',
    header: t('table.columns.vehicle'),
    render: (row) => (
      <VehicleCell
        name={row.vehicleName}
        extraCount={row.vehicleExtraCount}
        hasEngine={row.vehicleHasEngine}
        isEnclosed={row.vehicleIsEnclosed}
      />
    ),
    className: 'w-52',
  }
}

function originDestinationColumn(t: TFunction): DataTableColumn<ShipmentRow> {
  return {
    key: 'originDestination',
    header: t('table.columns.originDestination'),
    render: (row) => (
      <LocationCell origin={row.origin} destination={row.destination} />
    ),
  }
}

function tariffColumn(t: TFunction): DataTableColumn<ShipmentRow> {
  return {
    key: 'tariff',
    header: t('table.columns.tariff'),
    render: (row) => (
      <ShipmentTariffCell
        shipmentId={row.shipmentId}
        deposit={row.deposit}
        convertToStage={SHIPMENT_STAGES.QUOTE}
      />
    ),
  }
}

function shipDateColumn(t: TFunction): DataTableColumn<ShipmentRow> {
  return {
    key: 'shipDate',
    header: t('table.columns.shipDate'),
    render: (row) => <DateCell date={row.shipDate} size="sm" />,
  }
}

export function useLeadsColumns({
  showOwner,
}: UseLeadsColumnsOptions): DataTableColumn<ShipmentRow>[] {
  const { t } = useTranslation()
  return [
    idColumn(t),
    createdColumn(t),
    noteColumn(t),
    ...(showOwner ? [ownerColumn(t)] : []),
    customerColumn(t),
    vehicleColumn(t),
    originDestinationColumn(t),
    tariffColumn(t),
    shipDateColumn(t),
  ]
}
